import { BottomSheet } from '~/modules/elements/akui/bottom-sheet';
import { useLanguageList } from '~/modules/songs/hooks/use-language-list';
import isE2E from '~/modules/utils/is-e2-e';
import { languageCode } from '~/modules/utils/language-name-to-iso-code';
import { CheckBox } from '~/routes/remote-mic/panels/microphone/remote-controls';

interface Props {
  open: boolean;
  onClose: () => void;
  excludedLanguages: string[];
  languageList: ReturnType<typeof useLanguageList>;
  onListChange: (list: string[]) => void;
}

// A language carrying fewer songs than this is noise in a list somebody is scanning with a thumb.
const MIN_SONGS_COUNT = isE2E() ? 0 : 30;

export default function LanguageFilter({ open, onClose, languageList, excludedLanguages, onListChange }: Props) {
  const filteredLanguageList = languageList.filter((lang) => lang.count > MIN_SONGS_COUNT);

  const excludeLanguage = (name: string) => {
    const visibleExcludedLanguages = excludedLanguages.filter((lang) =>
      filteredLanguageList.find((l) => l.name === lang),
    );
    // The first tap means "only this one", which is what somebody reaching for a language actually
    // wants; after that each tap adds or removes one, and emptying the exclusions means "all".
    if (visibleExcludedLanguages.length === 0) {
      onListChange(languageList.filter((lang) => lang.name !== name).map((lang) => lang.name));
    } else if (visibleExcludedLanguages.includes(name)) {
      onListChange(excludedLanguages.filter((lang) => lang !== name));
    } else if (visibleExcludedLanguages.length === filteredLanguageList.length - 1) {
      onListChange([]);
    } else {
      onListChange([...excludedLanguages, name]);
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Languages">
      <div className="flex flex-col gap-2" data-test="languages-container">
        {filteredLanguageList.map(({ name, count }) => {
          const isExcluded = excludedLanguages.length > 0 && excludedLanguages.includes(name);

          return (
            <button
              key={name}
              type="button"
              onClick={() => excludeLanguage(name)}
              data-test={name}
              data-inactive={isExcluded}
              data-active={excludedLanguages.length > 0 && !isExcluded}
              className={`flex h-15 shrink-0 cursor-pointer items-center gap-3 rounded-2xl border-none bg-white/6 px-4.5 text-left text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)] transition-opacity duration-150 ${
                isExcluded ? 'opacity-45' : ''
              }`}>
              <CheckBox checked={!isExcluded} />
              <span className="min-w-0 flex-1 truncate text-[1.0625rem]">{name}</span>
              <span className="shrink-0 text-[0.875rem] text-white/50">{count}</span>
              <span
                data-test="language-code"
                className="flex h-6 w-9 shrink-0 items-center justify-center rounded bg-white/10 text-[0.6875rem] font-bold uppercase">
                {languageCode(name) ?? '—'}
              </span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={onClose}
          data-test="close-language-filter"
          className="bg-active mt-0.5 flex h-16 shrink-0 cursor-pointer items-center justify-center rounded-[1.125rem] border-none text-[1.1875rem] font-bold text-black transition-transform duration-100 active:scale-[0.98]">
          Done
        </button>
      </div>
    </BottomSheet>
  );
}
